import { resourceActions } from '@oplog/resource-redux';
import { Box, Button, Flex, Icon } from '@oplog/express';
import React, { FC, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { ResourceType } from '../../../models';
import { StoreState } from '../../../store/initState';
import { urls } from '../../../routers/urls';
import { PackingModals } from '../../../store/global/packingStore';
import { PickingPhases, RASPickingModals } from '../../../store/global/rasPickingStore';
import { initialReturnState } from '../../../store/global/returnStore';
import { ScanPackageModals } from '../../pages/ApprovedOrderSlamStation/bones/ScanPackageColumn';
import { InfoMessageBoxState } from '../InfoMessageBox/InfoMessageBox';

const intlKey = 'TouchScreen';

interface IMoreActionScreen {
  state?: any;
  action?: any;
  setIsMoreActionsOpen?: (status: boolean) => void;
  setIsManuelBarcodeInputOpen?: (status: boolean) => void;
  toggleModalState?: (name: ScanPackageModals, status?: boolean) => void;
}

const MoreActionScreen: FC<IMoreActionScreen> = ({
  state,
  action,
  setIsMoreActionsOpen,
  setIsManuelBarcodeInputOpen,
  toggleModalState,
}): ReactElement => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // The problem solver station has path-based tabs (/:tab?), so match by base path.
  const isRasProblemSolverStation = location.pathname.startsWith(urls.rasProblemSolverStation.split('/:')[0]);

  // RAS order-based pick: checkProduct publishes ITEM_PICKED to RAS on success, so while it is in
  // flight (product just scanned, phase still Product) flagging the item missing would send RAS a
  // contradictory ITEM_PICKED + ITEM_NOTFOUND pair — hide the button for that window.
  const rasOrderBasedPickCheckProductBusy = useSelector(
    (storeState: StoreState) => storeState.resources[ResourceType.RasOrderBasedPickCheckProduct]?.isBusy
  );

  return (
    <>
      <Box
        onClick={() => action?.setIsMoreActionsOpen(false) || setIsMoreActionsOpen?.(false)}
        position="fixed"
        width="100vw"
        height="100vh"
        left={0}
        top={0}
        zIndex={3}
        opacity={0.6}
        bg="palette.black"
      />
      <Flex
        position="absolute"
        flexDirection="column"
        transform={
          location.pathname === urls.returnStation && state.orderNumber && !state.isReturnCompleted
            ? 'translateY(-330px)'
            : state && state.orderNumber && !state.isOrderCompleted && !state.isMissing && !state.isCancelled
              ? 'translateY(-240px)'
              : location.pathname === urls.singleItemFastPackingStation
                ? 'translateY(-44px)'
                : isRasProblemSolverStation
                  ? 'translateY(-120px)'
                  : 'translateY(-210px)'
        }
        width="auto"
        height="auto"
        zIndex={3}
        ml={
          [urls.rasPutAwayStation, urls.rasPickingStation].includes(location.pathname) || isRasProblemSolverStation
            ? 32
            : undefined
        }
      >
        <Button
          onClick={() => {
            action?.setIsMoreActionsOpen(false) || setIsMoreActionsOpen?.(false);
            action?.setIsManuelBarcodeInputOpen(true) || setIsManuelBarcodeInputOpen?.(true);
          }}
          variant="alternative"
          fontFamily="Jost"
          fontWeight={500}
          mb={16}
        >
          <Icon name="fal fa-keyboard" mr={11} />
          {t(`${intlKey}.ActionButtons.BarcodeInput`)}
        </Button>
        {[urls.packingStation, urls.hovPackingStation].includes(location.pathname) &&
          state.orderNumber &&
          !state.isOrderCompleted &&
          !state.isMissing &&
          !state.isCancelled && (
            <Button
              onClick={() => {
                action.setIsMoreActionsOpen(false);
                action.toggleModalState(
                  state.isPreApprovedMissing ? PackingModals.PreApprovedMissing : PackingModals.MissingItem
                );
                state.isPreApprovedMissing && action.setIsCompletableWithMissing(true);
              }}
              variant="danger"
              fontFamily="Jost"
              fontWeight={500}
              mb={16}
            >
              <Icon name="far fa-eye" mr={11} />
              {t(`${intlKey}.ActionButtons.Missing`)}
            </Button>
          )}
        {/* RAS order-based picking: "Ürün Eksik" shows at the Product phase — i.e. after the cell is
            scanned and before the product is scanned. The operator flags a missing item precisely on the
            "scan product" screen; it must NOT appear at the Cell/POD phase (before scanning the cell).
            Not gated on operation cluster: production operations run the 'Common' cluster (not 'Trendyol'),
            so a cluster gate would hide the button for the real users of order-based picking. */}
        {location.pathname === urls.rasOrderBasedPickingStation &&
          state?.phases?.Product &&
          !rasOrderBasedPickCheckProductBusy &&
          state?.salesOrderId &&
          state?.cellLabel &&
          state?.product?.barcodes && (
            <Button
              onClick={() => {
                action.setIsMoreActionsOpen(false);
                // Show a spinner while the (multi-second) missing-item request is in flight; the
                // success/error response handler replaces it. Without this the screen sits blank.
                action.callInfoMessageBox({
                  state: InfoMessageBoxState.Scan,
                  text: t('TouchScreen.RASPickingStation.Processing'),
                  timer: 0,
                });
                dispatch(
                  resourceActions.resourceRequested(ResourceType.RasOrderBasedPickMissingItem, {
                    payload: {
                      stationLabel: state.station.label,
                      salesOrderId: state.salesOrderId,
                      pickListId: state.pickListId,
                      cellLabel: state.cellLabel,
                      productBarcode: state.product?.barcodes?.split(',')[0],
                    },
                  })
                );
              }}
              variant="danger"
              fontFamily="Jost"
              fontWeight={500}
              mb={16}
            >
              <Icon name="far fa-times-circle" mr={11} />
              {t(`${intlKey}.ActionButtons.MissingItem`)}
            </Button>
          )}
        {/* RAS put-away: toggle between the tote-based flow and Direct PutAway (Direkt Yerleştirme). */}
        {location.pathname === urls.rasPutAwayStation && (
          <Button
            onClick={() => {
              action.setIsMoreActionsOpen(false);
              const nextMode = !state.isDirectPutAwayMode;
              action.setIsDirectPutAwayMode(nextMode);
              nextMode
                ? localStorage.setItem('isDirectPutAwayMode', 'true')
                : localStorage.removeItem('isDirectPutAwayMode');
              nextMode && state.modals.ForceAddTote && action.toggleModalState(RASPickingModals.ForceAddTote, false);
              // A half-scanned product or an interrupted tote add/remove belongs to the previous
              // mode; drop it and restart from the product scan.
              action.setProduct({});
              action.setToteToBeAdded('');
              action.setToteToBeRemoved('');
              action.setSelectedSlot(undefined);
              state.phases.Cell && action.togglePhaseState(PickingPhases.Product, true);
            }}
            disabled={
              (state.infoMessageBox?.state === InfoMessageBoxState.Scan && Boolean(state.infoMessageBox?.text)) ||
              Object.values(RASPickingModals)
                .filter(modal => modal !== RASPickingModals.ForceAddTote)
                .some(modal => state.modals[modal])
            }
            variant={state.isDirectPutAwayMode ? 'warning' : 'alternative'}
            fontFamily="Jost"
            fontWeight={500}
            mb={16}
          >
            <Icon name="far fa-level-down" mr={11} />
            {t(`${intlKey}.ActionButtons.${state.isDirectPutAwayMode ? 'DirectPutAwayOff' : 'DirectPutAwayOn'}`)}
          </Button>
        )}
        {location.pathname === urls.returnStation && state.orderNumber && !state.isReturnCompleted && (
          <>
            <Button
              onClick={() => action.clearState(initialReturnState)}
              variant="primary"
              fontFamily="Jost"
              fontWeight={500}
              mb={16}
            >
              <Icon name="far fa-bring-forward" mr={11} />
              {t(`${intlKey}.ActionButtons.AbortProcess`)}
            </Button>
            <Button
              onClick={() => {
                action.setPreviousBoxItems(state.boxItems);
                action.setBoxItems(state.previousBoxItems);
                action.setIsMoreActionsOpen(false);
              }}
              variant="warning"
              fontFamily="Jost"
              fontWeight={500}
              mb={16}
            >
              <Icon name="far fa-undo" mr={11} />
              {t(`${intlKey}.ActionButtons.RevertLastAction`)}
            </Button>
          </>
        )}
        <Button
          onClick={() => {
            action?.setIsMoreActionsOpen(false) || setIsMoreActionsOpen?.(false);
            localStorage.removeItem('stationAddress');
            navigate(urls.stationLogin);
          }}
          variant="info"
          fontFamily="Jost"
          fontWeight={500}
          mb={16}
          color="palette.white"
        >
          <Icon name="far fa-exchange-alt" mr={11} />
          {t(`${intlKey}.ActionButtons.ChangeStation`)}
        </Button>
        <Button
          onClick={() => {
            action?.setIsMoreActionsOpen(false) || setIsMoreActionsOpen?.(false);
            action?.toggleModalState(PackingModals.Logout) || toggleModalState?.(ScanPackageModals.Logout, true);
          }}
          disabled={
            location.pathname === urls.rasPutAwayStation
              ? state.phases.Cell ||
                Object.values(RASPickingModals)
                  .filter(modal => modal !== RASPickingModals.ForceAddTote)
                  .some(modal => state.modals[modal])
              : location.pathname === urls.rasPickingStation
                ? state.phases.Tote || Object.keys(RASPickingModals).some(modal => state.modals[modal])
                : null
          }
          variant="light"
          fontFamily="Jost"
          fontWeight={500}
          mb={16}
          color="palette.softBlue_dark"
        >
          <Icon name="far fa-arrow-from-right" mr={11} />
          {t(`${intlKey}.ActionButtons.Logout`)}
        </Button>
      </Flex>
    </>
  );
};

export default MoreActionScreen;
